<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { ElMessage } from 'element-plus';
import {
  getStatsOverview,
  getStatsTrends,
  getRecentMessages,
  type StatsOverview,
  type StatsTrends,
  type RecentMessage,
} from '../api';

const loading = ref(false);
const overview = ref<StatsOverview | null>(null);
const trends = ref<StatsTrends | null>(null);
const messages = ref<RecentMessage[]>([]);
const trendDays = ref<7 | 14 | 30>(7);
const activeSeries = ref('users');

// 指标卡定义
const cards = computed(() => {
  const t = overview.value?.totals;
  if (!t) return [];
  return [
    { label: '总用户', value: t.users, color: '#409eff' },
    { label: '认证圈友', value: t.approvedUsers, color: '#67c23a' },
    { label: '帖子', value: t.posts, color: '#e6a23c' },
    { label: '评论', value: t.comments, color: '#909399' },
    { label: '部落', value: t.tribes, color: '#b88230' },
    { label: '头条/训练营', value: t.channelPosts, color: '#f56c6c' },
    { label: '私信总数', value: t.messages, color: '#9c27b0' },
    { label: '待处理举报', value: t.pendingReports, color: '#ff5247' },
  ];
});

// 当前选中趋势序列
const currentSeries = computed(() => trends.value?.series.find((s) => s.key === activeSeries.value) ?? null);
const trendMax = computed(() => {
  const d = currentSeries.value?.data ?? [];
  return Math.max(1, ...d);
});

// 用户状态分布(柱状)
const statusBars = computed(() => {
  const s = overview.value?.userAnalysis.byStatus;
  if (!s) return [];
  const max = Math.max(1, s.PENDING, s.APPROVED, s.REJECTED, s.FROZEN);
  return [
    { label: '待审核', value: s.PENDING, max, color: '#e6a23c' },
    { label: '已认证', value: s.APPROVED, max, color: '#67c23a' },
    { label: '已驳回', value: s.REJECTED, max, color: '#909399' },
    { label: '已冻结', value: s.FROZEN, max, color: '#f56c6c' },
  ];
});

const schoolMax = computed(() => Math.max(1, ...(overview.value?.userAnalysis.topSchools ?? []).map((s) => s.count)));

async function loadTrends() {
  try {
    trends.value = await getStatsTrends(trendDays.value);
  } catch (e) {
    ElMessage.error(normalize(e));
  }
}

function normalize(e: unknown): string {
  return (e as { message?: string })?.message ?? '加载失败';
}

async function reload() {
  loading.value = true;
  try {
    const [ov, msgs] = await Promise.all([getStatsOverview(), getRecentMessages(30)]);
    overview.value = ov;
    messages.value = msgs.items;
    await loadTrends();
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    loading.value = false;
  }
}

function fmtTime(iso: string): string {
  if (!iso) return '';
  return iso.replace('T', ' ').slice(0, 16);
}

onMounted(reload);
</script>

<template>
  <div v-loading="loading" class="stats">
    <div class="bar">
      <span class="muted">平台数据总览</span>
      <el-button size="small" @click="reload">刷新</el-button>
    </div>

    <!-- 指标卡 -->
    <div class="cards">
      <div v-for="c in cards" :key="c.label" class="card">
        <div class="card-value" :style="{ color: c.color }">{{ c.value }}</div>
        <div class="card-label">{{ c.label }}</div>
      </div>
    </div>

    <div class="grid">
      <!-- 趋势柱状图 -->
      <div class="panel">
        <div class="panel-head">
          <span class="panel-title">新增趋势</span>
          <div class="panel-tools">
            <el-radio-group v-model="activeSeries" size="small">
              <el-radio-button v-for="s in trends?.series ?? []" :key="s.key" :value="s.key">{{ s.name }}</el-radio-button>
            </el-radio-group>
            <el-select v-model="trendDays" size="small" style="width: 96px; margin-left: 8px" @change="loadTrends">
              <el-option :value="7" label="近7天" />
              <el-option :value="14" label="近14天" />
              <el-option :value="30" label="近30天" />
            </el-select>
          </div>
        </div>
        <div v-if="currentSeries" class="chart">
          <div v-for="(v, i) in currentSeries.data" :key="i" class="chart-col">
            <div class="chart-bar-wrap">
              <div class="chart-val">{{ v }}</div>
              <div class="chart-bar" :style="{ height: (v / trendMax * 100) + '%' }" />
            </div>
            <div class="chart-label">{{ trends?.labels[i] }}</div>
          </div>
        </div>
      </div>

      <!-- 用户分析 -->
      <div class="panel">
        <div class="panel-head"><span class="panel-title">用户分析</span></div>
        <div class="stat-row">
          <div class="stat-item"><span class="muted">认证率</span><b>{{ overview?.userAnalysis.certifiedRate }}%</b></div>
          <div class="stat-item"><span class="muted">今日新增</span><b>{{ overview?.userAnalysis.newToday }}</b></div>
          <div class="stat-item"><span class="muted">本周新增</span><b>{{ overview?.userAnalysis.newThisWeek }}</b></div>
        </div>
        <div class="hbars">
          <div v-for="b in statusBars" :key="b.label" class="hbar">
            <span class="hbar-label">{{ b.label }}</span>
            <div class="hbar-track"><div class="hbar-fill" :style="{ width: (b.value / b.max * 100) + '%', background: b.color }" /></div>
            <span class="hbar-val">{{ b.value }}</span>
          </div>
        </div>
      </div>

      <!-- 学校 Top5 -->
      <div class="panel">
        <div class="panel-head"><span class="panel-title">认证圈友学校 Top5</span></div>
        <div v-if="overview?.userAnalysis.topSchools.length" class="hbars">
          <div v-for="s in overview.userAnalysis.topSchools" :key="s.name" class="hbar">
            <span class="hbar-label">{{ s.name }}</span>
            <div class="hbar-track"><div class="hbar-fill" :style="{ width: (s.count / schoolMax * 100) + '%', background: '#409eff' }" /></div>
            <span class="hbar-val">{{ s.count }}</span>
          </div>
        </div>
        <el-empty v-else description="暂无数据" :image-size="60" />
      </div>

      <!-- 内容分析 -->
      <div class="panel">
        <div class="panel-head"><span class="panel-title">内容分析</span></div>
        <div class="content-grid">
          <div class="content-item"><b>{{ overview?.contentAnalysis.visiblePosts }}</b><span class="muted">已发布帖</span></div>
          <div class="content-item"><b>{{ overview?.contentAnalysis.pendingPosts }}</b><span class="muted">待审帖</span></div>
          <div class="content-item"><b>{{ overview?.contentAnalysis.blockedPosts }}</b><span class="muted">已下架帖</span></div>
          <div class="content-item"><b>{{ overview?.contentAnalysis.tribePosts }}</b><span class="muted">部落帖</span></div>
        </div>
      </div>
    </div>

    <!-- 最近私信巡查 -->
    <div class="panel">
      <div class="panel-head"><span class="panel-title">最近私信(内容巡查)</span></div>
      <el-table :data="messages" size="small" max-height="360" empty-text="暂无私信">
        <el-table-column prop="senderName" label="发送者" width="140">
          <template #default="{ row }">{{ row.senderName || row.senderId }}</template>
        </el-table-column>
        <el-table-column prop="type" label="类型" width="80" />
        <el-table-column prop="content" label="内容" show-overflow-tooltip />
        <el-table-column label="时间" width="150">
          <template #default="{ row }">{{ fmtTime(row.createdAt) }}</template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<style scoped>
.stats { padding: 4px; }
.bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.muted { color: #909399; font-size: 13px; }

.cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px; }
.card { background: #fff; border: 1px solid #ebeef5; border-radius: 8px; padding: 16px; text-align: center; }
.card-value { font-size: 28px; font-weight: 700; line-height: 1.2; }
.card-label { margin-top: 6px; font-size: 13px; color: #606266; }

.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
.panel { background: #fff; border: 1px solid #ebeef5; border-radius: 8px; padding: 16px; }
.panel-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.panel-title { font-weight: 600; font-size: 15px; }
.panel-tools { display: flex; align-items: center; }

/* 柱状图 */
.chart { display: flex; align-items: flex-end; height: 200px; gap: 6px; }
.chart-col { flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; }
.chart-bar-wrap { flex: 1; width: 100%; display: flex; flex-direction: column; justify-content: flex-end; align-items: center; }
.chart-val { font-size: 11px; color: #909399; margin-bottom: 2px; }
.chart-bar { width: 60%; min-height: 2px; background: linear-gradient(180deg, #66b1ff, #409eff); border-radius: 4px 4px 0 0; transition: height 0.3s; }
.chart-label { margin-top: 6px; font-size: 11px; color: #909399; }

/* 横条 */
.hbars { display: flex; flex-direction: column; gap: 12px; }
.hbar { display: flex; align-items: center; gap: 10px; }
.hbar-label { width: 96px; font-size: 13px; color: #606266; text-align: right; flex-shrink: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hbar-track { flex: 1; height: 16px; background: #f0f2f5; border-radius: 8px; overflow: hidden; }
.hbar-fill { height: 100%; border-radius: 8px; min-width: 2px; transition: width 0.3s; }
.hbar-val { width: 40px; font-size: 13px; font-weight: 600; }

.stat-row { display: flex; gap: 24px; margin-bottom: 16px; }
.stat-item { display: flex; flex-direction: column; gap: 2px; }
.stat-item b { font-size: 22px; color: #303133; }

.content-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.content-item { display: flex; flex-direction: column; gap: 4px; padding: 12px; background: #f7f8fa; border-radius: 6px; }
.content-item b { font-size: 24px; color: #303133; }
</style>
